import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useToast } from './ToastContext';
import { useTranslation } from '../../i18n';
import { gameUsesTeams } from '../../utils/teamGames';
import type { WebSocketAdapter } from '../../utils/WebSocketAdapter';

type FeelingAlonePlayer = {
  id: string;
  name: string;
  score: number;
  avatar?: string;
  isBot?: boolean;
};

type FeelingAloneAiContextValue = {
  /** Host-only mid-game CTA after Q1 until dismissed or an AI joins. */
  shouldShow: boolean;
  adding: boolean;
  addAiPlayer: () => void;
  dismiss: () => void;
};

const FeelingAloneAiContext = createContext<FeelingAloneAiContextValue | null>(null);

export interface FeelingAloneAiProviderProps {
  children: React.ReactNode;
  roomName: string;
  socket: WebSocketAdapter;
  players: FeelingAlonePlayer[];
  gameType?: string | null;
  /** Peak / current main-question number (1-based). Null between questions is fine — peak is retained. */
  questionNumber?: number | null;
  isBonusLevel?: boolean;
  gameState: 'waiting' | 'playing' | 'finished';
}

/**
 * Host-only “Feeling alone?” → Add AI player.
 * Shows after the first question (from Q2). Stays until the host dismisses (X) or adds AI.
 */
export const FeelingAloneAiProvider: React.FC<FeelingAloneAiProviderProps> = ({
  children,
  roomName,
  socket,
  players,
  gameType,
  questionNumber,
  isBonusLevel = false,
  gameState,
}) => {
  const { showToast } = useToast();
  const { t } = useTranslation();
  const [adding, setAdding] = useState(false);
  const [peakQuestionNumber, setPeakQuestionNumber] = useState(0);
  const [dismissed, setDismissed] = useState(false);
  const roomNameRef = useRef(roomName);
  const pendingTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Reset per room / new game
  useEffect(() => {
    if (roomNameRef.current !== roomName || gameState === 'waiting') {
      roomNameRef.current = roomName;
      setPeakQuestionNumber(0);
      setDismissed(false);
      setAdding(false);
      if (pendingTimeoutRef.current) {
        clearTimeout(pendingTimeoutRef.current);
        pendingTimeoutRef.current = null;
      }
    }
  }, [roomName, gameState]);

  useEffect(() => {
    if (typeof questionNumber === 'number' && questionNumber > peakQuestionNumber) {
      setPeakQuestionNumber(questionNumber);
    }
  }, [questionNumber, peakQuestionNumber]);

  const botCount = players.filter((p) => p.isBot).length;
  const humanCount = players.filter((p) => !p.isBot).length;

  useEffect(() => {
    if (botCount > 0 && adding) {
      setAdding(false);
      if (pendingTimeoutRef.current) {
        clearTimeout(pendingTimeoutRef.current);
        pendingTimeoutRef.current = null;
      }
    }
  }, [botCount, adding]);

  useEffect(() => {
    const onRoomError = (payload: { message?: string; code?: string }) => {
      if (!adding) return;
      setAdding(false);
      if (pendingTimeoutRef.current) {
        clearTimeout(pendingTimeoutRef.current);
        pendingTimeoutRef.current = null;
      }
      if (payload?.message) {
        showToast(payload.message, 'warning');
      }
    };
    socket.on('room-error', onRoomError);
    return () => {
      socket.off('room-error', onRoomError);
    };
  }, [socket, adding, showToast]);

  useEffect(() => {
    return () => {
      if (pendingTimeoutRef.current) clearTimeout(pendingTimeoutRef.current);
    };
  }, []);

  const addAiPlayer = useCallback(() => {
    if (adding || botCount >= 3) {
      if (botCount >= 3) showToast(t('lobby.ai.maxBots'), 'warning');
      return;
    }
    setAdding(true);
    socket.emit('spawn-ai-bot', { roomName });
    if (pendingTimeoutRef.current) clearTimeout(pendingTimeoutRef.current);
    pendingTimeoutRef.current = setTimeout(() => {
      setAdding(false);
      pendingTimeoutRef.current = null;
    }, 12_000);
  }, [adding, botCount, roomName, socket, showToast, t]);

  const dismiss = useCallback(() => {
    setDismissed(true);
  }, []);

  const shouldShow = useMemo(() => {
    if (gameState !== 'playing') return false;
    if (dismissed) return false;
    if (isBonusLevel) return false;
    if (gameUsesTeams(gameType)) return false;
    if (botCount > 0) return false;
    if (humanCount !== 1) return false;
    // After first question has passed (Q2+ rendered, or peak retained across result gaps)
    if (peakQuestionNumber < 2) return false;
    return true;
  }, [
    gameState,
    dismissed,
    isBonusLevel,
    gameType,
    botCount,
    humanCount,
    peakQuestionNumber,
  ]);

  const value = useMemo(
    () => ({ shouldShow, adding, addAiPlayer, dismiss }),
    [shouldShow, adding, addAiPlayer, dismiss]
  );

  return (
    <FeelingAloneAiContext.Provider value={value}>{children}</FeelingAloneAiContext.Provider>
  );
};

export function useFeelingAloneAi(): FeelingAloneAiContextValue | null {
  return useContext(FeelingAloneAiContext);
}
